import {
  combineLatestTyped,
  filterNotUndefined,
  IRect,
  IRectType,
  mergeTyped,
  roundRectToInt,
  toAbsoluteRect,
  useDataStream,
  useEventAsStream,
  useStreamEffect,
  useStreamValue,
  withLatestValuesFrom
} from 'annotation-tools/utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { updateProjectImagePartially } from '../../../../api/fetch-functions/project-image';
import { token$ } from '../../../../global-state/auth-state';
import { fetchProjectImageListOf } from '../../../../global-state/database/project-image-list';
import { imageId$, projectId$ } from '../../../../global-state/router-id';
import { IProjectImageInfoType } from '../../../../types/record/image-info';

type CropRectReducerState = {
  lastOp: 'canvas' | 'input';
  data: IRectType;
};

export const useCropRect = (
  cropRectRelativeInput$: Observable<IRectType | undefined>,
  imageInfo$: Observable<IProjectImageInfoType>,
  setPostResultType: (value: 'error' | 'success') => void
) => {
  const [
    // dummy comment to control prettier
    cropRectRelativeFromCanvas$,
    setCropRectRelativeFromCanvas
  ] = useEventAsStream<IRectType>();

  const cropRectRelativeMerged$ = useDataStream<
    CropRectReducerState | undefined
  >(
    undefined,
    mergeTyped(
      combineLatestTyped(
        cropRectRelativeInput$.pipe(filterNotUndefined()),
        imageId$ // ページ遷移時にinputで初期化
      ).pipe(map(([a]) => ({ lastOp: 'input' as const, data: a }))),
      cropRectRelativeFromCanvas$.pipe(
        map(a => ({ lastOp: 'canvas' as const, data: a }))
      )
    )
  );

  useStreamEffect(
    cropRectRelativeFromCanvas$.pipe(
      withLatestValuesFrom(token$, projectId$, imageInfo$)
    ),
    ([newCropRect, [token, projectId, image]]) => {
      if (token === undefined) return;
      updateProjectImagePartially(token)(
        projectId,
        image.id
      )({
        cropRect: roundRectToInt(toAbsoluteRect(IRect(image.size))(newCropRect))
      }).then(res => {
        setPostResultType(res.type);
        fetchProjectImageListOf(projectId);
      });
    }
  );

  const cropRectRelative$ = useDataStream<IRectType | undefined>(
    undefined,
    cropRectRelativeMerged$.pipe(map(a => a?.data))
  );

  const cropRectRelative = useStreamValue(cropRectRelative$);

  return {
    cropRectRelativeFromCanvas$,
    setCropRectRelativeFromCanvas,
    cropRectRelative$,
    cropRectRelative
  };
};
